import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import Header from '../../components/ui/Header';

import MetricsCard from './components/MetricsCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import WelcomeHeader from './components/WelcomeHeader';
import Icon from '../../components/AppIcon';

import ProjectCarousel from './components/ProjectCarousel';



const Dashboard = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const handleCreateNewProject = () => {
    window.location.href = '/create-project';
  };

  // Extended mock data with twice as many projects for each status
  const allProjects = [
    // Active/Approved Projects
    {
      id: 1,
      clientName: "Sarah Mitchell",
      clientAvatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Sarah Mitchell, a woman with shoulder-length brown hair and warm smile",
      projectType: "Custom Engagement Ring",
      status: "approved",
      progress: 100,
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      feedbackCount: 3,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Design approved and sent to production",
      lastActivityTime: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
    },
    {
      id: 2,
      clientName: "Michael Rodriguez",
      clientAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Michael Rodriguez, a man with dark hair and friendly expression",
      projectType: "Wedding Band Set",
      status: "approved",
      progress: 100,
      createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
      feedbackCount: 2,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Final approval received, ready for crafting",
      lastActivityTime: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
    },
    {
      id: 3,
      clientName: "Jennifer Kim",
      clientAvatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Jennifer Kim, an Asian woman with black hair in a business setting",
      projectType: "Anniversary Necklace",
      status: "approved",
      progress: 100,
      createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
      feedbackCount: 4,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Client confirmed final design specifications",
      lastActivityTime: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
    },
    {
      id: 4,
      clientName: "Robert Thompson",
      clientAvatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Robert Thompson, a middle-aged man with gray hair and glasses",
      projectType: "Custom Cufflinks",
      status: "approved",
      progress: 100,
      createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
      feedbackCount: 1,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Production started for engraved cufflinks",
      lastActivityTime: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000)
    },

    // Pending Projects
    {
      id: 5,
      clientName: "Emma Thompson",
      clientAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Emma Thompson, a young woman with blonde hair and bright smile",
      projectType: "Diamond Tennis Bracelet",
      status: "pending",
      progress: 75,
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      feedbackCount: 2,
      hasNewFeedback: true,
      newFeedbackCount: 1,
      lastActivity: "Awaiting client response on diamond selection",
      lastActivityTime: new Date(Date.now() - 2 * 60 * 60 * 1000)
    },
    {
      id: 6,
      clientName: "David Chen",
      clientAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of David Chen, an Asian man with short black hair in business attire",
      projectType: "Vintage-Style Engagement Ring",
      status: "pending",
      progress: 60,
      createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
      feedbackCount: 3,
      hasNewFeedback: true,
      newFeedbackCount: 2,
      lastActivity: "Client reviewing vintage setting options",
      lastActivityTime: new Date(Date.now() - 1 * 60 * 60 * 1000)
    },
    {
      id: 7,
      clientName: "Lisa Wang",
      clientAvatar: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Lisa Wang, an Asian woman with long dark hair and professional appearance",
      projectType: "Pearl Drop Earrings",
      status: "pending",
      progress: 45,
      createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
      feedbackCount: 1,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Initial design shared, awaiting feedback",
      lastActivityTime: new Date(Date.now() - 3 * 60 * 60 * 1000)
    },
    {
      id: 8,
      clientName: "Mark Johnson",
      clientAvatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Mark Johnson, a man with brown hair and casual business attire",
      projectType: "Men\'s Signet Ring",
      status: "pending",
      progress: 30,
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      feedbackCount: 2,
      hasNewFeedback: true,
      newFeedbackCount: 1,
      lastActivity: "Pending approval on engraving design",
      lastActivityTime: new Date(Date.now() - 4 * 60 * 60 * 1000)
    },

    // In Review Projects
    {
      id: 9,
      clientName: "Anna Martinez",
      clientAvatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Anna Martinez, a Hispanic woman with curly hair and warm expression",
      projectType: "Sapphire Pendant",
      status: "in-review",
      progress: 80,
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      feedbackCount: 1,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Design team reviewing gemstone arrangement",
      lastActivityTime: new Date(Date.now() - 5 * 60 * 60 * 1000)
    },
    {
      id: 10,
      clientName: "James Wilson",
      clientAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of James Wilson, a man with light brown hair and professional demeanor",
      projectType: "Anniversary Band",
      status: "in-review",
      progress: 65,
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      feedbackCount: 2,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Quality control reviewing specifications",
      lastActivityTime: new Date(Date.now() - 6 * 60 * 60 * 1000)
    },
    {
      id: 11,
      clientName: "Sophie Brown",
      clientAvatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Sophie Brown, a young woman with auburn hair and professional styling",
      projectType: "Emerald Cocktail Ring",
      status: "in-review",
      progress: 70,
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      feedbackCount: 0,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Technical review of emerald setting",
      lastActivityTime: new Date(Date.now() - 8 * 60 * 60 * 1000)
    },
    {
      id: 12,
      clientName: "Alex Rivera",
      clientAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Alex Rivera, a Hispanic man with dark hair and confident expression",
      projectType: "Custom Watch Chain",
      status: "in-review",
      progress: 55,
      createdAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000),
      feedbackCount: 3,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Engineering review for chain durability",
      lastActivityTime: new Date(Date.now() - 7 * 60 * 60 * 1000)
    },

    // Rejected Projects
    {
      id: 13,
      clientName: "Rachel Green",
      clientAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Rachel Green, a woman with blonde hair styled professionally",
      projectType: "Art Deco Brooch",
      status: "rejected",
      progress: 40,
      createdAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000),
      feedbackCount: 4,
      hasNewFeedback: true,
      newFeedbackCount: 2,
      lastActivity: "Client requested complete style change",
      lastActivityTime: new Date(Date.now() - 5 * 60 * 60 * 1000)
    },
    {
      id: 14,
      clientName: "Thomas Lee",
      clientAvatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Thomas Lee, an Asian man with professional business attire",
      projectType: "Diamond Stud Earrings",
      status: "rejected",
      progress: 25,
      createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
      feedbackCount: 2,
      hasNewFeedback: true,
      newFeedbackCount: 1,
      lastActivity: "Size specifications need major revision",
      lastActivityTime: new Date(Date.now() - 9 * 60 * 60 * 1000)
    },
    {
      id: 15,
      clientName: "Maria Garcia",
      clientAvatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Maria Garcia, a Hispanic woman with dark hair and elegant presentation",
      projectType: "Charm Bracelet",
      status: "rejected",
      progress: 35,
      createdAt: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000),
      feedbackCount: 3,
      hasNewFeedback: false,
      newFeedbackCount: 0,
      lastActivity: "Design direction needs complete overhaul",
      lastActivityTime: new Date(Date.now() - 12 * 60 * 60 * 1000)
    },
    {
      id: 16,
      clientName: "Kevin Park",
      clientAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      alt: "Professional headshot of Kevin Park, an Asian man with glasses and business casual attire",
      projectType: "Titanium Wedding Ring",
      status: "rejected",
      progress: 20,
      createdAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000),
      feedbackCount: 1,
      hasNewFeedback: true,
      newFeedbackCount: 1,
      lastActivity: "Material preference changed to gold",
      lastActivityTime: new Date(Date.now() - 10 * 60 * 60 * 1000)
    }
  ];

  // Mock data for metrics
  const metricsData = [
    {
      title: "Active Projects",
      value: "12",
      change: "+3",
      changeType: "positive",
      icon: "FolderOpen",
      description: "3 new projects this week"
    },
    {
      title: "Pending Approvals",
      value: "5",
      change: "-2",
      changeType: "positive",
      icon: "Clock",
      description: "2 approvals received today"
    },
    {
      title: "Client Interactions",
      value: "28",
      change: "+12",
      changeType: "positive",
      icon: "MessageSquare",
      description: "High engagement this week"
    },
    {
      title: "Completion Rate",
      value: "94%",
      change: "+5%",
      changeType: "positive",
      icon: "TrendingUp",
      description: "Above average performance"
    }
  ];

  // Mock data for recent activities
  const recentActivities = [
    {
      id: 1,
      type: "approved",
      clientName: "Sarah Mitchell",
      clientAvatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      description: "Approved the engagement ring design",
      projectName: "Custom Engagement Ring",
      timestamp: new Date(Date.now() - 30 * 60 * 1000)
    },
    {
      id: 2,
      type: "feedback",
      clientName: "David Chen",
      clientAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      description: "Left feedback on cufflink design",
      projectName: "Custom Cufflinks",
      feedback: "Love the overall design but could we make the engraving more prominent?",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000)
    },
    {
      id: 3,
      type: "viewed",
      clientName: "Emma Thompson",
      clientAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      description: "Viewed the diamond necklace design",
      projectName: "Diamond Necklace",
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000)
    },
    {
      id: 4,
      type: "shared",
      clientName: "Michael Rodriguez",
      clientAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      description: "Design link was shared",
      projectName: "Wedding Band Set",
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000)
    },
    {
      id: 5,
      type: "rejected",
      clientName: "Lisa Wang",
      clientAvatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      description: "Requested changes to bracelet design",
      projectName: "Tennis Bracelet",
      feedback: "The clasp design needs to be more secure and elegant.",
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000)
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Helmet>
        <title>Dashboard - JewelThread</title>
        <meta name="description" content="Manage your jewelry design projects, review client feedback, and collaborate with your team." />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {/* Enhanced Welcome Header Section */}
          <div className="bg-gradient-to-r from-card via-card to-accent/5 border-2 border-accent/30 rounded-xl p-8 shadow-lg">
            <WelcomeHeader userName="John Doe" />
          </div>

          {/* Enhanced Metrics Overview Section */}
          <div className="bg-gradient-to-r from-card to-primary/5 border-2 border-primary/20 rounded-xl p-6 shadow-lg">
            <div className="mb-4">
              <h2 className="text-xl font-heading text-foreground flex items-center gap-2">
                <Icon name="BarChart3" size={24} className="text-primary" />
                Performance Metrics
              </h2>
              <p className="text-muted-foreground">Track your design business performance</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {metricsData?.map((metric, index) => (
                <MetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  description={metric?.description}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Enhanced Projects Section with Status Carousels */}
            <div className="lg:col-span-2 space-y-6">
              {/* Active Projects Carousel */}
              <ProjectCarousel
                projects={allProjects}
                status="active"
                title="Active Projects"
                icon="CheckCircle"
                color="success"
              />
              
              {/* Pending Projects Carousel */}
              <ProjectCarousel
                projects={allProjects}
                status="pending"
                title="Pending Approval"
                icon="Clock"
                color="warning"
              />
              
              {/* In Review Projects Carousel */}
              <ProjectCarousel
                projects={allProjects}
                status="in-review"
                title="In Review"
                icon="Eye"
                color="accent"
              />
              
              {/* Rejected Projects Carousel */}
              <ProjectCarousel
                projects={allProjects}
                status="rejected"
                title="Needs Revision"
                icon="RefreshCw"
                color="error"
              />
            </div>

            {/* Enhanced Sidebar */}
            <div className="space-y-8">
              {/* Enhanced Quick Actions */}
              <div className="bg-gradient-to-br from-card to-accent/5 border-2 border-accent/30 rounded-xl shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-heading text-foreground flex items-center gap-2">
                        <Icon name="Zap" size={20} className="text-accent" />
                        Quick Actions
                      </h2>
                      <p className="text-sm text-muted-foreground">Fast access to key features</p>
                    </div>
                  </div>
                  <QuickActions />
                </div>
              </div>
              
              {/* Enhanced Activity Feed */}
              <div className="bg-gradient-to-br from-card to-info/5 border-2 border-info/20 rounded-xl shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-heading text-foreground flex items-center gap-2">
                        <Icon name="Activity" size={20} className="text-info" />
                        Recent Activity
                      </h2>
                      <p className="text-sm text-muted-foreground">Latest client interactions</p>
                    </div>
                  </div>
                  <ActivityFeed activities={recentActivities} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;